import { create } from "zustand";
import type { AppError } from "@/data/errors";
import { subscribeCurrentTruth } from "@/data/firestore/currentTruthRepository";
import type {
  Branding,
  BusinessProfile,
  BusinessTruth,
  LoadStatus,
  NotificationPreferences,
} from "@/domain/types";

export type CustomerSettingsView = {
  profile: BusinessProfile;
  branding: Branding;
  notifications: NotificationPreferences;
  publicConfig: { publicSlug: string };
};
type State = {
  customerId: string | null;
  truth: BusinessTruth | null;
  settings: CustomerSettingsView | null;
  status: LoadStatus;
  error: AppError | null;
  start: (customerId: string) => void;
  stop: () => void;
};
let unsubscribe: (() => void) | undefined;
let generation = 0;
function view(truth: BusinessTruth): CustomerSettingsView {
  return {
    profile: truth.business,
    branding: truth.business.branding,
    notifications: truth.account.notificationPreferences,
    publicConfig: { publicSlug: truth.business.publicSlug },
  };
}

export const useCustomerStore = create<State>((set, get) => ({
  customerId: null,
  truth: null,
  settings: null,
  status: "idle",
  error: null,
  start: (customerId) => {
    if (get().customerId === customerId && unsubscribe) return;
    unsubscribe?.();
    const current = ++generation;
    set({ customerId, truth: null, settings: null, status: "loading", error: null });
    unsubscribe = subscribeCurrentTruth(customerId, {
      next: (truth) => {
        if (current === generation && get().error?.code !== "access")
          set({ truth, settings: view(truth), status: "ready", error: null });
      },
      error: (error) => {
        if (current === generation) set({ truth: null, settings: null, status: "error", error });
      },
    });
  },
  stop: () => {
    generation++;
    unsubscribe?.();
    unsubscribe = undefined;
    set({ customerId: null, truth: null, settings: null, status: "idle", error: null });
  },
}));
